import { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import AuthPage from './pages/auth/AuthPage';
import AuthCallbackPage from './pages/auth/AuthCallbackPage';
import { Navigate } from 'react-router-dom';
import CategoryPage from './pages/CategoryPage';
import ArticlePage from './pages/ArticlePage';
import NotFoundPage from './pages/NotFoundPage';
import CourtsHubPage from './pages/CourtsHubPage';
import LawFirmsPage from './pages/LawFirmsPage';
import NetworkStatus from './components/common/NetworkStatus';
import ScrollToTop from './components/common/ScrollToTop';
import TopStoriesPage from './pages/TopStoriesPage';
import GiftPremiumPage from './pages/GiftPremiumPage';
import SubscribePage from './pages/SubscribePage';
import AdvertisePage from './pages/AdvertisePage';
import { LanguageProvider } from './contexts/LanguageContext';
import { SupabaseProvider, DataSourceIndicator } from './contexts/SupabaseContext';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ProfilePage from './pages/auth/ProfilePage';
import PrivateRoute from './components/auth/PrivateRoute';
import { supabase, initializeSupabaseAuth } from './lib/supabase';
import { LoadingProvider } from './contexts/LoadingContext';
import AuthDebug from './components/common/AuthDebug';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import { ROUTES } from './constants/routes';
import BareActsPage from './pages/BareActsPage';
import BareActPage from './pages/BareActDetailPage';
import { preloadCommonData } from './services/bareActService';

function App() {
  const [isInitializing, setIsInitializing] = useState(true);
  
  // Define the error handler
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Application error:', error, errorInfo);
    // You could send this to an error reporting service
  };

  useEffect(() => {
    // This function removes the initial HTML loader
    const removeInitialLoader = () => {
      const loader = document.querySelector('.app-loading');
      if (loader) {
        // Add fade-out animation
        loader.classList.add('app-loading-fade');
        
        // Remove from DOM after animation completes
        setTimeout(() => {
          loader.parentNode?.removeChild(loader);
        }, 300);
      }
    };

    // Initialize app with better error handling
    const initializeApp = async () => {
      try {
        console.log("Initializing application...");
        
        // Use Promise.race to ensure we don't wait too long for auth initialization
        const authPromise = initializeSupabaseAuth();
        const timeoutPromise = new Promise(resolve => 
          setTimeout(() => resolve({ error: 'Auth initialization timed out, continuing anyway' }), 3000)
        );
        
        const authResult = await Promise.race([authPromise, timeoutPromise]);
        
        if (
          typeof authResult === 'object' &&
          authResult !== null &&
          'error' in authResult
        ) {
          const resultWithError = authResult as { error: unknown };
          console.warn("Auth initialization warning:", resultWithError.error);
          // Continue despite auth errors - don't block the app
        }
        
        // Handle email verification redirect
        if (window.location.hash) {
          try {
            const { data, error } = await supabase.auth.getUser();
            
            if (data?.user && !error) {
              console.log("User verified email successfully:", data.user);
              
              // Clear the hash fragment
              window.history.replaceState(null, document.title, window.location.pathname + window.location.search);
              
              // Fire notification event
              window.dispatchEvent(new CustomEvent('auth-notification', { 
                detail: { 
                  message: 'Email verified successfully! You are now signed in.',
                  type: 'success'
                } 
              }));
            }
          } catch (error) {
            console.error("Error handling auth redirect:", error);
          }
        }
      } catch (error) {
        console.error("Failed to initialize application:", error);
      } finally {
        // Always complete initialization
        setIsInitializing(false);
        // Remove the initial loader
        removeInitialLoader();
      }
    };

    initializeApp();

    // Fallback: ensure the loader is removed even if initialization hangs
    const fallbackTimer = setTimeout(() => {
      setIsInitializing(false);
      removeInitialLoader();
      console.warn('Used fallback timeout to remove loader');
    }, 5000);

    return () => clearTimeout(fallbackTimer);
    preloadCommonData();
  }, []);

  // Check if we're in development mode
  const isDevelopment = import.meta.env.MODE === 'development';

  // Keep the initial loader active until initialization is complete
  if (isInitializing) {
    return null; // This will keep the HTML loader from index.html visible
  }

  return (
    <ErrorBoundary onError={handleError}>
      <LoadingProvider>
        <SupabaseProvider>
          <AuthProvider>
            <LanguageProvider>
              <Layout>
                <ScrollToTop />
                <ErrorBoundary>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/category/:categorySlug" element={<CategoryPage />} />
                    <Route path="/article/:articleSlug" element={<ArticlePage />} />
                    <Route path="/courts" element={<CourtsHubPage />} />
                    <Route path="/law-firms" element={<LawFirmsPage />} />
                    <Route path="/law-firms/:subsection" element={<LawFirmsPage />} />
                    <Route path="/category/top-stories" element={<TopStoriesPage />} />
                    <Route path="/gift-premium" element={<GiftPremiumPage />} />
                    <Route path="/subscribe-premium" element={<SubscribePage />} />
                    <Route path="/advertise" element={<AdvertisePage />} />
                    <Route path={ROUTES.ABOUT} element={<AboutPage />} /> 
                    <Route path={ROUTES.CONTACT} element={<ContactPage />} /> 
                    <Route path="/bare-acts" element={<BareActsPage />} />
                    <Route path="/bare-acts/:actSlug" element={<BareActPage />} />
                    
                    {/* Auth routes */}
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/auth-callback" element={<AuthCallbackPage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
                    
                    <Route path="/login" element={<Navigate to="/auth" replace />} />
                    <Route path="/signup" element={<Navigate to="/auth?mode=register" replace />} />
                    
                    {/* 404 route - must be last */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </ErrorBoundary>
                <NetworkStatus />
                {/* Only show debug components in development mode */}
                {isDevelopment && (
                  <>
                    <DataSourceIndicator />
                    <AuthDebug />
                  </>
                )}
              </Layout>
            </LanguageProvider>
          </AuthProvider>
        </SupabaseProvider>
      </LoadingProvider>
    </ErrorBoundary>
  );
}

export default App;